import axios from "axios";
import { jwtDecode } from "jwt-decode";
import { refreshMsalIdTokenCoalesced } from "services/msalTokenRefresh";

let requestInterceptorId = null;

/** Refresh the bearer ID token this many seconds before JWT `exp` (Azure B2C id tokens are short-lived). */
const PROACTIVE_REFRESH_SEC = 300;

function setAuthHeader(config, bearerToken) {
  const h = config.headers;
  if (h && typeof h.set === "function") {
    h.set("Authorization", `Bearer ${bearerToken}`);
  } else {
    config.headers = { ...h, Authorization: `Bearer ${bearerToken}` };
  }
}

/**
 * Installs a single axios request interceptor that attaches `Authorization: Bearer`
 * from `localStorage` on every request. Idempotent.
 *
 * When the stored ID token is missing or near expiry, runs MSAL silent refresh
 * so long-lived sessions (e.g. MFA "remember me") are not broken by hourly JWT expiry.
 *
 * Must not depend on MFA or React lifecycle: `AccountOwnerProvider` and other
 * siblings under `Routes` dispatch API calls before `mfa-validate` is set.
 */
export function ensureAxiosBearerInterceptor() {
  if (requestInterceptorId != null) {
    return;
  }
  requestInterceptorId = axios.interceptors.request.use(async (config) => {
    let token = localStorage.getItem("msal.idtoken");
    if (token) {
      try {
        const { exp } = jwtDecode(token);
        if (
          typeof exp === "number" &&
          exp * 1000 < Date.now() + PROACTIVE_REFRESH_SEC * 1000
        ) {
          token = await refreshMsalIdTokenCoalesced();
        }
      } catch {
        /* ignore decode errors; request proceeds with existing token */
      }
      setAuthHeader(config, token);
    }
    return config;
  });
}
